import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { passwordsSecurityValidator } from './validators/passwords-validator';
import { MatchPassword } from './validators/password-match';
import { ChangePasswordService } from '../services/change-password.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { timer } from 'rxjs';
import { Router } from '@angular/router';
import { TwoFactorToggleComponent } from './components/two-factor-toggle/two-factor-toggle.component';
import { MatDialog } from '@angular/material/dialog';
import { FormMessages } from '@app/utils/interfaces/errors-password.interface';
import { InputType } from '@app/utils/enums/input-type.enum';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { ValidationStatus } from '@app/utils/enums/validation-status.enum';
import { AppRoutes } from '@app/utils/enums/routes.enum';
import { ServerErrors } from '@app/utils/enums/http-errors.enum';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { AuthService } from '@app/services/auth/auth.service';

@Component({
  selector: 'app-user-profile-security',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    ReactiveFormsModule,
    MatIcon,
    IconComponent,
    TwoFactorToggleComponent,
  ],
  templateUrl: './user-profile-security.component.html',
  styleUrl: './user-profile-security.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileSecurityComponent implements OnInit {
  public readonly serverErrors = ServerErrors;
  public isPasswordVisible = [false, false, false];
  public inputType = InputType;
  public form!: FormGroup;
  public formSubmitted = signal(false);
  public messages = signal<FormMessages>({});
  public iconName = IconName;

  private readonly changePasswordService = inject(ChangePasswordService);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  public ngOnInit(): void {
    this.form = new FormGroup(
      {
        currentPassword: new FormControl('', [Validators.required]),
        newPassword: new FormControl('', [Validators.required, passwordsSecurityValidator()]),
        confirmPassword: new FormControl('', [Validators.required]),
      },
      { validators: MatchPassword.passwordMatch() },
    );
  }

  public togglePasswordVisibility(index: number): void {
    this.isPasswordVisible[index] = !this.isPasswordVisible[index];
  }

  public shouldShowErrors(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!control && (control.dirty || control.touched) && !!control.errors;
  }

  public isValidationValid(fieldName: string, errorKey: string): boolean {
    const control = this.form.get(fieldName);
    return !!control && !control.hasError(errorKey) && !!control.value;
  }

  public getValidationClass(field: string, errorKey: string): ValidationStatus {
    const control = this.form.get(field);
    if (!control) return ValidationStatus.INVALID;
    return !control.hasError(errorKey) && !!control.value
      ? ValidationStatus.VALID
      : ValidationStatus.INVALID;
  }

  public onCancel(): void {
    this.form.reset();
  }

  public onSubmit(): void {
    this.formSubmitted.set(true);
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    this.changePasswordService
      .changePassword(this.form.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          const dialogRef = this.openStatusDialog();
          this.form.reset();
          this.isPasswordVisible = [false, false, false];
          return dialogRef.afterClosed();
        }),
        tap(() => {
          this.authService.logout();
          this.router.navigate([AppRoutes.LOGIN]);
        }),
        catchError((error) => {
          const backendMessage = error.error?.message;
          const passwordNotCorrect = this.translate.instant(
            'user-profile.security.changePassword.errors.passwordNotCorrect',
          );
          this.messages.set({
            invalidCurrent: backendMessage === passwordNotCorrect,
            serverMessage: backendMessage !== passwordNotCorrect ? backendMessage : undefined,
          });
          return timer(3000).pipe(tap(() => this.clearSubmissionMessages()));
        }),
      )
      .subscribe();
  }

  public clearSubmissionMessages(): void {
    this.messages.set({});
    this.formSubmitted.set(false);
  }

  private openStatusDialog() {
    return this.dialog.open(StatusModalComponent, {
      data: {
        isSuccess: true,
        title: 'user-profile.security.changePassword.changingPassword',
        description: 'user-profile.security.changePassword.successMessage',
      },
    });
  }
}
